/**
 * Design tokens for the PoseRef UI.
 *
 * All magic colors, spacing values, and radii live here so every component
 * draws from a single source of truth.  When we move to a proper theme
 * provider or CSS-variable system later, this file is the only thing that
 * needs to change.
 */

// ---------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------

/** Primary accent blue — buttons, active states, sliders. */
export const COLOR_ACCENT = "#5aa0e8";

/** Active-state background tint (semi-transparent accent). */
export const COLOR_ACTIVE_BG = "rgba(60,130,246,0.2)";

/** Softer active-state background (used for toggles & highlighted rows). */
export const COLOR_ACTIVE_BG_SUBTLE = "rgba(60,130,246,0.15)";

/** Danger / destructive action foreground. */
export const COLOR_DANGER = "#e88";

/** Danger / destructive action background. */
export const COLOR_DANGER_BG = "rgba(220,60,60,0.1)";

/** Gold accent used for favourites / stars. */
export const COLOR_GOLD = "#f0c040";

/** Amber caution — active/pending operator state. */
export const COLOR_CAUTION = "#e8a832";
export const COLOR_CAUTION_BG = "rgba(232,168,50,0.15)";

/** Warning/amber accent used in severity pills and toasts. */
export const COLOR_WARNING = "#f0a030";

/** Error/red accent used in severity pills and toasts. */
export const COLOR_ERROR = "#e85454";

/** Modifier kind colors. */
export const COLOR_MOD_SUBSURF = "#6c8";
export const COLOR_MOD_SOLIDIFY = "#c8a050";
export const COLOR_MOD_ARRAY = "#6af";
export const COLOR_MOD_SKIN = "#e8a87c";
export const COLOR_MOD_INSTANCE = "#8fd19e";
export const COLOR_MOD_SHRINKWRAP = "#9ec4e8";
export const COLOR_MOD_CURVE = "#7db7d9";
export const COLOR_MOD_FOLLOW = "#b39ddb";
export const COLOR_MOD_NOISE = "#d8a0d0";
export const COLOR_MOD_BOOLEAN = "#d56b6b";
export const COLOR_MOD_SMOOTH = "#7dd4c8";
export const COLOR_MOD_BEVEL = "#c49a7d";

/** Armature icon color (scene graph). */
export const COLOR_ARMATURE = "#6c8";

/** Library / badge type colors. */
export const COLOR_TYPE_BLEND = "#c8a0e8";
export const COLOR_TYPE_POSE = "#e87070";

// -- Axis colors (shared by gizmo, constraint lines, touch palette) --------

export const COLOR_AXIS_X = "#e74c3c";
export const COLOR_AXIS_Y = "#2ecc71";
export const COLOR_AXIS_Z = "#5aa0e8";
export const COLOR_AXIS_X_HEX = 0xe74c3c;
export const COLOR_AXIS_Y_HEX = 0x2ecc71;
export const COLOR_AXIS_Z_HEX = 0x5aa0e8;

// -- Neutrals ---------------------------------------------------------------

/** Primary text on dark background. */
export const COLOR_TEXT = "#e8e8ed";

/** Text on accent-colored backgrounds (buttons, badges). */
export const COLOR_TEXT_ON_ACCENT = "#fff";

/** Secondary text (labels, descriptions). */
export const COLOR_TEXT_SECONDARY = "#ccc";

/** Tertiary / muted text (metadata, counts). */
export const COLOR_TEXT_MUTED = "#b0b0b4";

/** Dim text (section labels, hints). */
export const COLOR_TEXT_DIM = "#999";

/** Very dim text (disabled, placeholder). */
export const COLOR_TEXT_FAINT = "#808088";

/** Disabled foreground. */
export const COLOR_TEXT_DISABLED = "#707074";

/** Subtle control foreground (off-state toggles). */
export const COLOR_TEXT_SUBTLE = "#aaa";

/** Subtle surface background (input fields, inactive buttons). */
export const COLOR_SURFACE = "rgba(255,255,255,0.06)";

/** Faint border / divider. */
export const COLOR_BORDER = "rgba(255,255,255,0.08)";

/** Slightly stronger border (inputs, segmented controls). */
export const COLOR_BORDER_STRONG = "rgba(255,255,255,0.12)";

/** Even stronger border for prominent controls. */
export const COLOR_BORDER_ACCENT = "rgba(255,255,255,0.15)";

/** Modal/dialog backdrop overlay. */
export const COLOR_BACKDROP = "rgba(0,0,0,0.5)";

/** Search/setting highlight tint. */
export const COLOR_HIGHLIGHT_BG = "rgba(90,160,232,0.04)";

/** Application background. */
export const COLOR_BG = "#0e0e10";

/** Header-bar / dock background (slightly lifted from app bg). */
export const COLOR_HEADER_BG = "#18181c";

/** Viewport / scene area background. */
export const COLOR_VIEWPORT_BG = "#1a1a1e";

// -- Overlay / debug surfaces ----------------------------------------------

/** Opaque overlay for debug panels (console, memory, recorder, bevel). */
export const COLOR_DEBUG_OVERLAY_BG = "rgba(0,0,0,0.88)";

/** Hover/selected highlight tint for debug menu rows. */
export const COLOR_DEBUG_HIGHLIGHT_BG = "rgba(0,255,0,0.08)";

/** Brush cursor popover surface (slightly tinted neutral). */
export const COLOR_BRUSH_OVERLAY_BG = "rgba(20,20,24,0.92)";

// -- Severity tints (info / warning / error) -------------------------------
// Used by ViewportMessages, future Toast banners, and inline notification
// panels. Background is a 10%-opacity tint of the severity hue; border is a
// 25% tint. Foreground colors live in COLOR_ACCENT / COLOR_WARNING / COLOR_ERROR.

export const COLOR_INFO_TINT_BG = "rgba(90,160,232,0.10)";
export const COLOR_INFO_TINT_BORDER = "rgba(90,160,232,0.25)";
export const COLOR_WARNING_TINT_BG = "rgba(240,160,48,0.10)";
export const COLOR_WARNING_TINT_BORDER = "rgba(240,160,48,0.25)";
export const COLOR_ERROR_TINT_BG = "rgba(232,84,84,0.10)";
export const COLOR_ERROR_TINT_BORDER = "rgba(232,84,84,0.25)";

// -- Hand pose visualization -----------------------------------------------

/** Selected joint color in the hand schematic. */
export const COLOR_HAND_JOINT_SELECTED = "#40d8ff";

/** Constraint line color in the hand schematic. */
export const COLOR_HAND_LINE = "rgba(232,160,48,0.4)";

// ---------------------------------------------------------------------------
// Animation
// ---------------------------------------------------------------------------

/** Duration (ms) for panel collapse/expand transitions. */
export const COLLAPSE_MS = 200;

/** Duration (ms) of the toolbar-group highlight-on-mount animation. */
export const TOOLBAR_HIGHLIGHT_DURATION_MS = 2000;

/** Highlight color for toolbar groups appearing on mount. */
export const COLOR_TOOLBAR_HIGHLIGHT = "rgba(90, 160, 232, 0.12)";

// ---------------------------------------------------------------------------
// Spacing
// ---------------------------------------------------------------------------

/** Standard gap between toolbar groups & panel sections. */
export const SPACE_SM = 4;
export const SPACE_MD = 8;
export const SPACE_LG = 12;
export const SPACE_XL = 16;

/** Vertical offset from toolbar to popover panels. */
export const PANEL_BOTTOM_OFFSET = 80;

/** Side panel top offset (below the header bar). */
export const PANEL_TOP_OFFSET = 52;

/** Side panel horizontal inset. */
export const PANEL_SIDE_INSET = 16;

/** Default width of the right-side panel dock. */
export const DOCK_DEFAULT_WIDTH = 320;

/** Minimum resizable dock width. */
export const DOCK_MIN_WIDTH = 240;

/** Maximum resizable dock width. */
export const DOCK_MAX_WIDTH = 500;

/** Width of the dock in collapsed (icon-strip) mode. */
export const DOCK_COLLAPSED_WIDTH = 44;

// ---------------------------------------------------------------------------
// Radii
// ---------------------------------------------------------------------------

/** Glass panel outer radius (cards, modals). */
export const RADIUS_PANEL = 16;

/** Toolbar pill radius. */
export const RADIUS_TOOLBAR = 22;

/** Larger radius for toast pills and popover panels. */
export const RADIUS_LARGE = 12;

/** Header / dock corner radius. */
export const RADIUS_HEADER = 10;

/** Control / button radius. */
export const RADIUS_CONTROL = 8;

/** Small control radius (tags, mini-buttons). */
export const RADIUS_SM = 6;

// ---------------------------------------------------------------------------
// Tool primitive — uniform across every surface (ToolPalette, ToolbarStrip,
// HeaderBar, SidebarControls, ViewportCameraControls). See ui/tool/Tool.tsx.
// ---------------------------------------------------------------------------

/** Canonical tool-button size (locked — same on every surface). */
export const TOOL_SIZE = 32;

/** Icon size used inside a tool-button trigger. */
export const TOOL_ICON_SIZE = 15;

/** Icon size used inside a tool-flyout row (smaller — rows are text-led). */
export const TOOL_FLYOUT_ICON_SIZE = 14;

/** Minimum width of a tool-flyout popover. */
export const TOOL_FLYOUT_MIN_WIDTH = 140;

/** Gap between the surface edge and the flyout popover. */
export const TOOL_FLYOUT_GAP = 4;

/** Extra-small radius (badges, kbd chips, tiny indicators). */
export const RADIUS_XS = 4;

/** Pill radius for small status dots / modifier indicators. */
export const RADIUS_DOT = 3;

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

/** Page-level heading (login, account pages). */
export const FONT_SIZE_PAGE_TITLE = 22;
/** Sub-heading (library top bar, modal titles). */
export const FONT_SIZE_SUBHEADING = 16;
export const FONT_SIZE_MODAL_TITLE = 15;
export const FONT_SIZE_PANEL_TITLE = 14;
export const FONT_SIZE_BODY = 13;
export const FONT_SIZE_SMALL = 12;
/** Compact control labels (header dropdowns, toolbar items). */
export const FONT_SIZE_CAPTION = 11;
export const FONT_SIZE_SECTION = 10;
export const FONT_SIZE_TINY = 10;

// ---------------------------------------------------------------------------
// Z-index layers
// ---------------------------------------------------------------------------

export const Z_BACKDROP = 14;
export const Z_PANEL = 20;
export const Z_FLOATING = 21;
export const Z_TOOLBAR = 22;
export const Z_POPOVER = 25;
export const Z_MODAL = 30;
export const Z_OVERLAY = 100;
export const Z_DRAG = 200;
export const Z_DEBUG = 999;

// ---------------------------------------------------------------------------
// Responsive breakpoints
// ---------------------------------------------------------------------------

/** Mobile breakpoint — matches the .mobile-hscroll media query in index.html. */
export const MOBILE_MAX_WIDTH = 768;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

import { type CSSProperties, useEffect, useState } from "react";
import {
  useIsEdgePanActive,
  useIsSliderDragging,
} from "./uiInteractionStore.ts";

/**
 * Returns the resolved `env(safe-area-inset-bottom)` value in pixels.
 * Reads once on mount by measuring a temporary hidden element.
 */
export function useSafeAreaInsetBottom(): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const el = document.createElement("div");
    el.style.cssText =
      "position:fixed;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom,0px)";
    document.body.appendChild(el);
    setValue(parseFloat(getComputedStyle(el).paddingBottom) || 0);
    el.remove();
  }, []);
  return value;
}

/** Returns `true` when the viewport is at or below `MOBILE_MAX_WIDTH`. */
export function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(
    () => window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const onChange = () => setMobile(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

/** Returns `true` when the user has requested reduced motion. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

/** How deep to dim overlay chrome while a slider is being dragged on mobile. */
export const CHROME_DIM_OPACITY = 0.18;
/**
 * How deep to dim overlay chrome while the gizmo is driving camera edge-pan
 * compensation. Deeper than the slider-drag fade — edge-pan is a sustained
 * "get out of the way, I'm working" moment during a continuous drag, so the
 * chrome should nearly disappear to maximise viewport visibility.
 */
export const CHROME_DIM_OPACITY_EDGE_PAN = 0.08;
/** Duration of the chrome dim fade. Matched to feel "intentional" — neither snappy nor laggy. */
export const CHROME_DIM_MS = 160;

/**
 * Returns style properties for overlay chrome that should fade out during
 * transient viewport gestures (so the viewport is visible underneath).
 *
 * Dims for either:
 *   - a slider drag on mobile (shallower fade — tactile feedback), OR
 *   - gizmo edge-pan compensation on any platform (deeper fade — sustained,
 *     unobstructed view while the camera follows a near-edge transform).
 *
 * Spread into the root `style` of any overlay UI (panels, popovers, header,
 * toolbars, FABs, pie menus, status bar, touch palettes). Place the spread
 * **last** so it overrides any default opacity/transition already on the root.
 *
 * Pass `baseTransition` if the consumer root already animates other
 * properties — the opacity transition will be appended so both animate.
 *
 * Intentionally does NOT set `pointerEvents: none` — the active slider relies
 * on `setPointerCapture` to continue receiving move events, and its DOM
 * subtree would be blocked by `pointer-events: none` on an ancestor.
 */
export function useChromeDim(baseTransition?: string): CSSProperties {
  const isMobile = useIsMobile();
  const sliderDragging = useIsSliderDragging();
  const edgePanActive = useIsEdgePanActive();
  const reducedMotion = useReducedMotion();

  let opacity = 1;
  if (edgePanActive) opacity = CHROME_DIM_OPACITY_EDGE_PAN;
  else if (isMobile && sliderDragging) opacity = CHROME_DIM_OPACITY;

  const opacityTransition = `opacity ${reducedMotion ? 0 : CHROME_DIM_MS}ms ease-out`;
  return {
    opacity,
    transition: baseTransition
      ? `${baseTransition}, ${opacityTransition}`
      : opacityTransition,
  };
}
